import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlarmClock, CheckCircle2, ClipboardList, Gauge, Plus, ShieldAlert, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { KpiStrip } from '../components/quality/KpiStrip';
import { AssignTaskDialog } from '../components/quality/admin/AssignTaskDialog';
import { TeamTab } from '../components/quality/admin/TeamTab';
import { TasksTab } from '../components/quality/admin/TasksTab';
import { ReportsTab } from '../components/quality/admin/ReportsTab';
import { TaskDetailSheet } from '../components/quality/admin/TaskDetailSheet';
import { DeleteTaskDialog, ExtendDeadlineDialog, NudgeAllDialog } from '../components/quality/admin/TaskDialogs';
import { UserHistorySheet } from '../components/quality/admin/UserHistorySheet';

const kpis = (o) => [
    { id: 'admin-open', label: 'Active tasks', value: o?.open, icon: ClipboardList, tone: 'text-violet-500', hint: o ? `${o.not_opened} not opened · ${o.in_progress} in progress` : '' },
    { id: 'admin-due', label: 'Due in 48h', value: o?.due_soon, icon: AlarmClock, tone: 'text-amber-500', hint: 'Not finished yet' },
    { id: 'admin-completion', label: 'Completion rate', value: o?.completion_rate == null ? null : `${o.completion_rate}%`, icon: CheckCircle2, tone: 'text-emerald-500', hint: o ? `${o.completed} done · ${o.missed} missed` : '' },
    { id: 'admin-avg', label: 'Avg task score', value: o?.avg_score, icon: Gauge, tone: 'text-indigo-500', hint: 'All time' },
    { id: 'admin-helpful', label: 'Found helpful', value: o?.helpful_pct == null ? null : `${o.helpful_pct}%`, icon: ThumbsUp, tone: 'text-fuchsia-500', hint: o ? `${o.feedback_count} response${o.feedback_count === 1 ? '' : 's'}` : '' },
];

const TABS = [{ id: 'team', label: 'Team' }, { id: 'tasks', label: 'Tasks' }, { id: 'reports', label: 'Reports' }];

const Denied = () => (
    <div className="flex flex-col items-center gap-3 rounded-2xl border p-12 text-center" data-testid="quality-admin-denied">
        <ShieldAlert className="h-10 w-10 text-muted-foreground" />
        <p className="text-muted-foreground">Quality Admin access is granted by your master admin.</p>
    </div>
);

export default function QualityAdmin() {
    const { api, user, isAdmin } = useAuth();
    const allowed = isAdmin || user?.feature_access?.quality_admin === true;
    const [tab, setTab] = useState('team');
    const [overview, setOverview] = useState(null);
    const [team, setTeam] = useState({ loading: true, users: [] });
    const [refreshKey, setRefreshKey] = useState(0);
    const [assignFor, setAssignFor] = useState(null);
    const [activeTask, setActiveTask] = useState(null);
    const [extendTask, setExtendTask] = useState(null);
    const [deleteTask, setDeleteTask] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [member, setMember] = useState(null);
    const [nudgeAll, setNudgeAll] = useState(null);
    const [nudging, setNudging] = useState(false);
    const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

    const nudge = async (task) => {
        try {
            const { data } = await api.post(`/quality-tasks/admin/tasks/${task.id}/nudge`);
            toast.success(`Reminder sent to ${task.user_name}`);
            setActiveTask((cur) => (cur && cur.id === data.id ? data : cur));
            refresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Could not send the reminder');
        }
    };

    const confirmNudgeAll = async () => {
        setNudging(true);
        try {
            const { data } = await api.post('/quality-tasks/admin/tasks/nudge', { task_ids: nudgeAll.map((t) => t.id) });
            toast.success(`${data.nudged} reminder${data.nudged === 1 ? '' : 's'} sent`, { description: data.skipped ? `${data.skipped} skipped (already nudged in the last 24h or finished)` : undefined });
            setNudgeAll(null);
            refresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Could not send reminders');
        } finally {
            setNudging(false);
        }
    };

    useEffect(() => {
        if (!allowed) return;
        api.get('/quality-tasks/admin/overview').then(({ data }) => setOverview(data)).catch(() => {});
        api.get('/quality-tasks/admin/team').then(({ data }) => setTeam({ loading: false, users: data.users })).catch(() => setTeam({ loading: false, users: [] }));
    }, [api, allowed, refreshKey]);

    const onExtended = (task) => {
        setExtendTask(null);
        setActiveTask((cur) => (cur && cur.id === task.id ? task : cur));
        refresh();
    };

    const confirmDelete = async () => {
        setDeleting(true);
        try {
            await api.delete(`/quality-tasks/admin/tasks/${deleteTask.id}`);
            toast.success('Task deleted');
            setDeleteTask(null);
            setActiveTask(null);
            refresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Could not delete the task');
        } finally {
            setDeleting(false);
        }
    };

    if (!allowed) return <MainLayout><Denied /></MainLayout>;

    return (
        <MainLayout>
            <div className="space-y-6" data-testid="quality-admin-page">
                <header className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <p className="font-mono text-xs uppercase tracking-[0.2em] text-violet-600 dark:text-violet-300">Quality</p>
                        <h1 className="mt-1 font-heading text-4xl font-bold tracking-tight sm:text-5xl">Quality Admin</h1>
                        <p className="mt-2 max-w-2xl text-muted-foreground">Turn call reviews into practice: assign simulations with a deadline, track completion and scores, and read what recruiters thought.</p>
                    </div>
                    <Button onClick={() => setAssignFor([])} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="assign-task-open-button">
                        <Plus className="mr-2 h-4 w-4" /> Assign task
                    </Button>
                </header>
                <KpiStrip items={kpis(overview)} testId="quality-admin-kpis" />
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList data-testid="quality-admin-tabs">
                        {TABS.map((t) => <TabsTrigger key={t.id} value={t.id} data-testid={`quality-admin-tab-${t.id}`}>{t.label}</TabsTrigger>)}
                    </TabsList>
                </Tabs>
                <AnimatePresence mode="wait">
                    <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }}>
                        {tab === 'team' && <TeamTab team={team.users} loading={team.loading} onAssign={setAssignFor} onHistory={setMember} />}
                        {tab === 'tasks' && <TasksTab api={api} refreshKey={refreshKey} currentUserId={user?.id} onView={setActiveTask} onExtend={setExtendTask} onDelete={setDeleteTask} onNudge={nudge} onNudgeAll={setNudgeAll} />}
                        {tab === 'reports' && <ReportsTab api={api} team={team.users} refreshKey={refreshKey} onOpenUser={setMember} />}
                    </motion.div>
                </AnimatePresence>
            </div>
            <AssignTaskDialog open={assignFor !== null} onOpenChange={(o) => !o && setAssignFor(null)} initialUserIds={assignFor} team={team.users} onAssigned={refresh} />
            <UserHistorySheet api={api} member={member} refreshKey={refreshKey} onOpenChange={(o) => !o && setMember(null)} onViewTask={(t) => { setMember(null); setActiveTask(t); }} />
            <TaskDetailSheet task={activeTask} currentUserId={user?.id} onOpenChange={(o) => !o && setActiveTask(null)} onExtend={setExtendTask} onDelete={setDeleteTask} onNudge={nudge} />
            <NudgeAllDialog tasks={nudgeAll} onOpenChange={(o) => !o && setNudgeAll(null)} onConfirm={confirmNudgeAll} busy={nudging} />
            <ExtendDeadlineDialog task={extendTask} onOpenChange={(o) => !o && setExtendTask(null)} onExtended={onExtended} />
            <DeleteTaskDialog task={deleteTask} onOpenChange={(o) => !o && setDeleteTask(null)} onConfirm={confirmDelete} busy={deleting} />
        </MainLayout>
    );
}
